/**
 * Shareable links to a single bar.
 *
 * These are the same URLs the iOS app shares, and the same ones
 * public/.well-known/apple-app-site-association claims — follow one on an
 * iPhone with Centmap installed and the app opens instead of the browser.
 */

/**
 * Always the production origin, never `window.location.origin`.
 *
 * A universal link only opens the app from the domain named in the app's
 * Associated Domains entitlement, so a link built from localhost or any
 * preview host would be a plain web link — copied, sent, and quietly broken
 * for the person who receives it.
 */
export const SITE_ORIGIN = 'https://centmap.app';

/** The canonical share URL for a bar. Mirrors the route in App.tsx. */
export function barShareUrl(barId: string): string {
  return `${SITE_ORIGIN}/centmap/bar/${encodeURIComponent(barId)}`;
}

/**
 * Copy text, reporting whether it worked so the caller can say so.
 *
 * The async Clipboard API needs a secure context, which production has. The
 * fallback covers the rest — older Safari, and anything served over plain
 * http — using the selection-based copy it replaced.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Denied, or no secure context. Fall through and try the old way.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Off-screen but still selectable; `display: none` cannot be copied from.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();

    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}
